/**
 * Cliente HTTP de baixo nível para a Football Data API (football-data.org v4).
 * Único ponto do projeto que monta a URL — nenhum outro arquivo deve
 * chamar `fetch` para dados de futebol diretamente (item 3 do escopo:
 * "Não espalhar chamadas HTTP pela aplicação").
 *
 * IMPORTANTE: isso chama `/api/football/...` (mesma origem, o proxy em
 * api/football/[...path].ts), NUNCA a football-data.org diretamente do
 * navegador — o provider bloqueia CORS em chamadas client-side. Ver o
 * comentário no arquivo do proxy pro diagnóstico completo. A chave
 * (VITE_FOOTBALL_DATA_API_KEY) não é mais usada aqui no cliente; ela é
 * lida pelo proxy, server-side.
 */

export type FootballApiErrorCode =
  | "missing_api_key"
  | "unauthorized"
  | "rate_limited"
  | "not_found"
  | "timeout"
  | "network_error"
  | "empty_response"
  | "unknown";

export class FootballApiError extends Error {
  readonly code: FootballApiErrorCode;
  readonly status?: number;

  constructor(code: FootballApiErrorCode, message: string, status?: number) {
    super(message);
    this.name = "FootballApiError";
    this.code = code;
    this.status = status;
  }
}

/** Mensagens amigáveis para exibir na UI — nunca expor detalhe técnico bruto (item 16 do escopo). */
export const FOOTBALL_API_ERROR_MESSAGES: Record<FootballApiErrorCode, string> = {
  missing_api_key: "A integração com dados reais de futebol ainda não foi configurada.",
  unauthorized: "Não foi possível autenticar com a fonte de dados de futebol.",
  rate_limited: "A fonte de dados de futebol está temporariamente sobrecarregada. Tente novamente em instantes.",
  not_found: "Não encontramos essa competição, clube ou partida.",
  timeout: "A fonte de dados de futebol demorou demais para responder.",
  network_error: "Sem conexão com a fonte de dados de futebol no momento.",
  empty_response: "A fonte de dados de futebol não retornou nenhuma informação para essa busca.",
  unknown: "Não foi possível carregar os dados de futebol agora.",
};

const REQUEST_TIMEOUT_MS = 8000;

/** Rota fixa e única — o caminho upstream vai como query string `?path=`, nunca em segmentos de URL (ver comentário em api/football.ts sobre por quê). */
const PROXY_ROUTE = "/api/football";

export const isFootballApiConfigured = true; // a checagem de chave agora acontece no proxy (server-side)

/**
 * GET genérico contra o proxy `/api/football?path=...`. Lança sempre
 * `FootballApiError` (nunca deixa um erro bruto de rede/fetch escapar pra
 * camada de domínio), conforme item 16 do escopo.
 */
export async function footballApiGet<T>(path: string, searchParams?: Record<string, string | number | undefined>): Promise<T> {
  const url = new URL(PROXY_ROUTE, window.location.origin);
  url.searchParams.set("path", path);
  if (searchParams) {
    for (const [key, value] of Object.entries(searchParams)) {
      if (value !== undefined) url.searchParams.set(key, String(value));
    }
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  let response: Response;
  try {
    response = await fetch(url.toString(), { signal: controller.signal, cache: "no-store" });
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      throw new FootballApiError("timeout", "Tempo limite excedido ao consultar a Football Data API.");
    }
    throw new FootballApiError("network_error", `Falha de rede ao consultar o proxy de dados de futebol: ${String(err)}`);
  } finally {
    clearTimeout(timeoutId);
  }

  if (response.status === 401 || response.status === 403) {
    throw new FootballApiError("unauthorized", "Chave da Football Data API inválida ou expirada.", response.status);
  }
  if (response.status === 404) {
    throw new FootballApiError("not_found", "Recurso não encontrado na Football Data API.", response.status);
  }
  if (response.status === 429) {
    throw new FootballApiError("rate_limited", "Limite de requisições da Football Data API atingido.", response.status);
  }
  if (response.status === 500) {
    // Erro do próprio proxy (ver api/football/[...path].ts) — provavelmente
    // VITE_FOOTBALL_DATA_API_KEY ausente nas env vars do servidor na Vercel.
    throw new FootballApiError("missing_api_key", "Integração de futebol não configurada no servidor.", response.status);
  }
  if (response.status === 502 || response.status === 504) {
    throw new FootballApiError("network_error", "O proxy não conseguiu alcançar a Football Data API.", response.status);
  }
  if (!response.ok) {
    throw new FootballApiError("unknown", `Football Data API retornou status ${response.status}.`, response.status);
  }

  let data: unknown;
  try {
    data = await response.json();
  } catch {
    throw new FootballApiError("empty_response", "Resposta vazia ou inválida da Football Data API.");
  }

  if (data === null || data === undefined) {
    throw new FootballApiError("empty_response", "A Football Data API não retornou dados.");
  }

  return data as T;
}
